/**
 * Piezo Client Constants for browser-side Piezo bundle assembly.
 */

/**
 * Piezo protocol version — V2 triggers Witness-required verification path
 */
export const PiezoProtocolVersion = 2;

/**
 * Used as a prefix for console.debug in PiezoClient for debugging purposes
 */
export const PiezoDebugPrefix = '[OpsTumbler][Piezo]';

/**
 * Metadata of the Piezo IndexedDB database
 */
export const PiezoIndexedDBMetadata = {
  db: {
    name: 'OpsTumbler-Piezo',
    version: 1,
  },
  table: {
    name: 'PiezoResources',
    /**
     * Name of the property used for key path (IndexedDB's primary key)
     */
    key: 'id',
    /**
     * Value for the key path
     */
    keyValue: 'userData',
  },
};

/**
 * Algorithm for a Piezo cryptographic key-pair to be passed into SubtleCrypto's generateKey
 */
export const PiezoKeyPairAlgorithm: EcKeyGenParams = {
  name: 'ECDSA',
  namedCurve: 'P-384',
};

/**
 * - Minimum amount of unique permits required for a user to be permitted to run a Piezo tool
 * - 2 unique permits means from 2 different Notary Clerks
 */
export const PIEZO_MINIMUM_UNIQUE_PERMITS_REQUIREMENT = 2;

/**
 * Version of the parsed-index cache. Bump it whenever the cached policyIndex/toolIndex shape or the
 * permit-parsing logic changes, so older cached indexes are rebuilt instead of trusted.
 */
export const PARSED_CACHE_VERSION = 1;

// ── Worker pool warm-up wait ──────────────────────────────────────────
// ── Bundle trimming ───────────────────────────────────────────────────
/**
 * Notary clerk families. Piezod requires the embedded permits to span every family listed here, so the
 * trim takes one permit per family. Family comes from clerkEndpoint (see getClerkFamily). Change this
 * list only on word from the Piezo team, and update PIEZO_TRIM_PERMITS_PER_POLICY to match.
 */
export const PIEZO_CLERK_FAMILIES = ['alps', 'cascades'] as const;

/**
 * Permits embedded per policy: one per clerk family. A literal rather than PIEZO_CLERK_FAMILIES.length
 * because it is only correct for a topology the Piezo team confirmed, so it must not silently follow an
 * edit to that list — the permitTrim constants test asserts the two agree.
 *
 * Used only as the threshold below which trimming cannot shrink anything.
 */
export const PIEZO_TRIM_PERMITS_PER_POLICY = 2;

// ── Principal-derived policy resolution ───────────────────────────────
/**
 * Minimum number of DISTINCT issuer decks that must attest a principal tag before we trust it. The
 * operator holds one deck per Okapi issuer, so if their deck count drops below this no role resolves
 * and nothing signs. A UX-side filter only; piezod remains the authority and may require more.
 */
export const PIEZO_MINIMUM_REQUIRED_ISSUERS = 4;

/** Prefix identifying a POSIX-group principal tag, e.g. "posix=searchservices-eng". */
export const PIEZO_POSIX_TAG_PREFIX = 'posix=';

// ── Policy ranking ────────────────────────────────────────────────────
/**
 * The operator's POSIX groups, most powerful first — POSITION IS THE RANK. Entries match
 * `PiezoParsedPolicyMetadata.principal` exactly, so the `posix=` prefix is required.
 * `aes-ops-elevated-access` is absent because it is an approver group, never a principal.
 */
const PIEZO_PRINCIPAL_POWER_ORDER = [
  'posix=searchservices-eng',
  'posix=aes-ops-support-l3',
  'posix=aes-ops-support-l2',
  'posix=aes-ops-support-l1',
  'posix=aws-stam-aes-tumbler',
  'posix=search-specialists-amer',
  'posix=searchservices-interns',
  'posix=searchservices-product',
  'posix=aes-ops-tumbler-on-demand',
] as const;

export const PIEZO_PRINCIPAL_POWER_RANK: Readonly<Record<string, number>> = Object.fromEntries(
  PIEZO_PRINCIPAL_POWER_ORDER.map((group, index) => [group, index + 1] as const),
);

/**
 * Rank given to a principal absent from PIEZO_PRINCIPAL_POWER_RANK: sorts after every known group.
 * Derived from the list length so adding or reordering a group needs no renumbering and cannot
 * collide with a real group's rank.
 */
export const PIEZO_UNKNOWN_PRINCIPAL_RANK = PIEZO_PRINCIPAL_POWER_ORDER.length + 1;

/**
 * Privilege order, least privileged first. POSITION IS THE TIER (ReadOnly 0, NoApproval 1). Matched
 * case-insensitively as a substring, not against full names, because names are stage- and part-suffixed
 * inconsistently. `NoApproval` is spelled in full so it cannot match `PeerApproval`.
 */
export const PIEZO_POLICY_TIERS = ['ReadOnly', 'NoApproval'] as const;

/** Tier for a name matching none of PIEZO_POLICY_TIERS: sorts last. Derived so it cannot drift. */
export const PIEZO_DEFAULT_POLICY_TIER = PIEZO_POLICY_TIERS.length;

/** Label of the navbar button that re-provisions Piezo; operator-facing messages point at it by this name. */
export const PIEZO_REFRESH_LABEL = 'Refresh Piezo';

/** What an operator should do when signing is unavailable. */
export const PIEZO_REFRESH_HINT = `Click the "${PIEZO_REFRESH_LABEL}" button in the navbar, then reload the page.`;

/** Shown when the server refused a tool the browser could not sign. Mirrors the server's text, pinned by test. */
export const PIEZO_SIGNING_FAILED_MESSAGE = `Piezo could not sign this request. ${PIEZO_REFRESH_HINT}`;
